import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@vehix/portal/components/ui/table';
import { Button } from '@vehix/portal/components/ui/button';
import { Switch } from '@vehix/portal/components/ui/switch';
import { Badge } from '@vehix/portal/components/ui/badge';
import { Checkbox } from '@vehix/portal/components/ui/checkbox';
import { ArrowDown, ArrowUp, Bell, CheckCircle, Pencil, Play, Trash2 } from 'lucide-react';
import { PipelineStatusBadge } from './PipelineStatusBadge';
import { ScheduleNextRunCell } from './ScheduleNextRunCell';
import { formatRelativeTime, formatTimestamp } from '../../utils/pipelineFormat';
import type { ScheduleRow, ScheduleSort, ScheduleSortKey } from '../../utils/scheduleRows';
import type { PipelineSchedule } from '../../services/pipelineOrchestrator';

interface PipelineSchedulesTableProps {
  rows: ScheduleRow[];
  now: Date;
  viewerZone: string;
  sort: ScheduleSort;
  onSort: (key: ScheduleSortKey) => void;
  onEdit: (schedule: PipelineSchedule) => void;
  onToggleEnabled: (schedule: PipelineSchedule) => void;
  onTrigger: (schedule: PipelineSchedule) => void;
  onDelete: (schedule: PipelineSchedule) => void;
  isCoolingDown: (id: string) => boolean;
  onOpenDetail: (row: ScheduleRow) => void;
  selectedIds: Set<string>;
  onToggleSelected: (id: string) => void;
  onToggleAll: (select: boolean) => void;
}

interface SortableHeadProps {
  label: string;
  sortKey: ScheduleSortKey;
  sort: ScheduleSort;
  onSort: (key: ScheduleSortKey) => void;
  className?: string;
}

const SortableHead: React.FC<SortableHeadProps> = ({
  label,
  sortKey,
  sort,
  onSort,
  className = ''
}) => {
  const active = sort.key === sortKey;
  const ariaSort = active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none';

  return (
    <TableHead className={className} aria-sort={ariaSort}>
      <button
        type='button'
        onClick={() => onSort(sortKey)}
        className='flex items-center gap-1 hover:text-foreground'
      >
        {label}
        {active &&
          (sort.dir === 'asc' ? (
            <ArrowUp className='h-3 w-3' />
          ) : (
            <ArrowDown className='h-3 w-3' />
          ))}
      </button>
    </TableHead>
  );
};

/** Zone names, capped — the full list goes in the title attribute. */
const ScopeCell: React.FC<{ row: ScheduleRow }> = ({ row }) => {
  if (row.zones.length === 0) {
    return <span className='text-xs text-muted-foreground'>—</span>;
  }
  const shown = row.zones.slice(0, 2).map(z => z.label);
  const extra = row.zones.length - shown.length;
  const full = row.zones.map(z => `${z.label} (${z.collectors.join(', ')})`).join('\n');

  return (
    <div className='text-xs' title={full}>
      <p className='font-medium'>
        {row.zones.length} zone{row.zones.length === 1 ? '' : 's'}
      </p>
      <p className='truncate text-muted-foreground'>
        {shown.join(', ')}
        {extra > 0 ? ` +${extra}` : ''}
      </p>
    </div>
  );
};

/** Who hears about runs, and about which outcomes. */
const NotifyCell: React.FC<{ schedule: PipelineSchedule }> = ({ schedule }) => {
  const recipients = schedule.notification_emails ?? [];
  const outcomes = [
    schedule.notify_on_success ? 'success' : null,
    schedule.notify_on_failure ? 'failure' : null
  ].filter(Boolean);

  if (recipients.length === 0 || outcomes.length === 0) {
    return (
      <span className='text-xs text-muted-foreground' title='No email notifications configured'>
        Off
      </span>
    );
  }

  return (
    <div
      className='flex items-center gap-1.5 text-xs'
      title={`On ${outcomes.join(' and ')} → ${recipients.join(', ')}`}
    >
      {schedule.notify_on_success && <CheckCircle className='h-3.5 w-3.5 text-green-500' />}
      {schedule.notify_on_failure && <Bell className='h-3.5 w-3.5 text-amber-500' />}
      <span className='text-muted-foreground'>{recipients.length}</span>
    </div>
  );
};

const LastRunCell: React.FC<{ schedule: PipelineSchedule }> = ({ schedule }) => {
  if (!schedule.last_triggered_at && !schedule.last_run_status) {
    return <span className='text-xs text-muted-foreground'>Never run</span>;
  }
  return (
    <div className='flex items-center gap-2'>
      {schedule.last_run_status && (
        <PipelineStatusBadge status={schedule.last_run_status} size='sm' />
      )}
      <span
        className='text-xs text-muted-foreground'
        title={formatTimestamp(schedule.last_triggered_at)}
      >
        {formatRelativeTime(schedule.last_triggered_at)}
      </span>
    </div>
  );
};

const RowActions: React.FC<{
  schedule: PipelineSchedule;
  coolingDown: boolean;
  onEdit: (s: PipelineSchedule) => void;
  onTrigger: (s: PipelineSchedule) => void;
  onDelete: (s: PipelineSchedule) => void;
}> = ({ schedule, coolingDown, onEdit, onTrigger, onDelete }) => (
  <div className='flex justify-end gap-1'>
    <Button
      variant='ghost'
      size='sm'
      className='h-7 px-2'
      onClick={() => onTrigger(schedule)}
      disabled={coolingDown}
      title={
        coolingDown ? `Cooling down — ${schedule.min_interval_minutes} min between runs` : 'Run now'
      }
    >
      <Play className='h-3 w-3' />
    </Button>
    <Button
      variant='ghost'
      size='sm'
      className='h-7 px-2'
      onClick={() => onEdit(schedule)}
      title='Edit'
    >
      <Pencil className='h-3 w-3' />
    </Button>
    <Button
      variant='ghost'
      size='sm'
      className='h-7 px-2 text-red-500 hover:text-red-600'
      onClick={() => onDelete(schedule)}
      title='Delete'
    >
      <Trash2 className='h-3 w-3' />
    </Button>
  </div>
);

type ScheduleTableRowProps = Pick<
  PipelineSchedulesTableProps,
  | 'now'
  | 'viewerZone'
  | 'onEdit'
  | 'onToggleEnabled'
  | 'onTrigger'
  | 'onDelete'
  | 'isCoolingDown'
  | 'onOpenDetail'
  | 'onToggleSelected'
> & { row: ScheduleRow; selected: boolean };

// eslint-disable-next-line max-lines-per-function
const ScheduleTableRow: React.FC<ScheduleTableRowProps> = ({
  row,
  selected,
  now,
  viewerZone,
  onEdit,
  onToggleEnabled,
  onTrigger,
  onDelete,
  isCoolingDown,
  onOpenDetail,
  onToggleSelected
}) => {
  const s = row.schedule;
  const cronTitle = `${s.cron_expression} (${s.time_zone})`;

  return (
    <TableRow className={s.is_enabled ? '' : 'bg-muted/30'} data-state={selected && 'selected'}>
      <TableCell className='pr-0'>
        <Checkbox
          checked={selected}
          onCheckedChange={() => onToggleSelected(s.id)}
          aria-label={`Select ${s.schedule_name}`}
        />
      </TableCell>

      <TableCell>
        <Switch
          checked={s.is_enabled}
          onCheckedChange={() => onToggleEnabled(s)}
          aria-label={s.is_enabled ? `Pause ${s.schedule_name}` : `Resume ${s.schedule_name}`}
        />
      </TableCell>

      <TableCell>
        <button
          type='button'
          onClick={() => onOpenDetail(row)}
          className='text-left text-sm font-medium hover:underline'
        >
          {s.schedule_name}
        </button>
        {s.tenants && <p className='text-xs text-muted-foreground'>{s.tenants.tenant_name}</p>}
      </TableCell>

      <TableCell>
        {row.cadence.kind === 'raw' ? (
          <code className='font-mono text-[11px] text-muted-foreground' title={cronTitle}>
            {row.cadence.text}
          </code>
        ) : (
          <span className='text-xs' title={cronTitle}>
            {row.cadence.text}
          </span>
        )}
      </TableCell>

      <TableCell>
        <ScheduleNextRunCell row={row} now={now} viewerZone={viewerZone} />
      </TableCell>

      <TableCell>
        <LastRunCell schedule={s} />
      </TableCell>

      <TableCell className='hidden max-w-[200px] lg:table-cell'>
        <ScopeCell row={row} />
      </TableCell>

      <TableCell className='hidden xl:table-cell'>
        <Badge variant='secondary' className='text-[10px] capitalize'>
          {s.pipeline_config.run_mode || 'auto'}
        </Badge>
      </TableCell>

      <TableCell className='hidden xl:table-cell'>
        <NotifyCell schedule={s} />
      </TableCell>

      <TableCell className='text-right'>
        <RowActions
          schedule={s}
          coolingDown={isCoolingDown(s.id)}
          onEdit={onEdit}
          onTrigger={onTrigger}
          onDelete={onDelete}
        />
      </TableCell>
    </TableRow>
  );
};

export const PipelineSchedulesTable: React.FC<PipelineSchedulesTableProps> = ({
  rows,
  sort,
  onSort,
  selectedIds,
  onToggleAll,
  ...rowProps
}) => {
  const allSelected = rows.length > 0 && rows.every(r => selectedIds.has(r.schedule.id));
  const someSelected = rows.some(r => selectedIds.has(r.schedule.id));

  return (
    <div className='overflow-auto rounded-md border'>
      <Table className='min-w-[900px]'>
        <TableHeader>
          <TableRow className='border-b bg-muted'>
            <TableHead className='w-[44px] pr-0'>
              <Checkbox
                checked={allSelected ? true : someSelected ? 'indeterminate' : false}
                onCheckedChange={() => onToggleAll(!allSelected)}
                aria-label={allSelected ? 'Clear selection' : 'Select all schedules'}
              />
            </TableHead>
            <TableHead className='w-[56px]' />
            <SortableHead label='Schedule' sortKey='name' sort={sort} onSort={onSort} />
            <TableHead>Cadence</TableHead>
            <SortableHead label='Next run' sortKey='next' sort={sort} onSort={onSort} />
            <SortableHead label='Last run' sortKey='lastRun' sort={sort} onSort={onSort} />
            <TableHead className='hidden lg:table-cell'>Scope</TableHead>
            <TableHead className='hidden xl:table-cell'>Mode</TableHead>
            <TableHead className='hidden xl:table-cell'>Notify</TableHead>
            <TableHead className='text-right'>Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map(row => (
            <ScheduleTableRow
              key={row.schedule.id}
              row={row}
              selected={selectedIds.has(row.schedule.id)}
              {...rowProps}
            />
          ))}
        </TableBody>
      </Table>
    </div>
  );
};
